import { createAction, createAsyncThunk } from '@reduxjs/toolkit';

import {
  httpCopyFromGlobalToTemplateAssets,
  httpGetGlobalAssets,
  httpGetGlobalAsset,
  httpDeleteAsset,
  httpDeleteGlobalAsset,
  httpUpdateGlobalAsset,
  httpGetAssets,
  httpSearchIcons,
  httpUnsplash,
  httpUploadAssets,
  httpGetFolders,
  httpCreateFolder,
} from 'api';
import {
  buildAssetsUrl,
  buildIconAssetsUrl,
  getErrorText,
  showErrorMessage,
} from 'helpers';

const copyFromGlobalToTemplateAssets = createAsyncThunk(
  'assets/copyFromGlobalToTemplateAssets',
  async ({ id, imageName, type }) => {
    try {
      const { data } = await httpCopyFromGlobalToTemplateAssets(
        id,
        imageName,
        type,
      );

      return data;
    } catch (error) {
      if (error?.response?.status !== 409) {
        showErrorMessage(getErrorText());
        throw error;
      }
    }
  },
);

const getGlobalAssets = createAsyncThunk(
  'assets/getGlobalAssets',
  async ({ type = 'image', folderId } = {}) => {
    try {
      const params = { type };
      if (folderId?.length) {
        params.folderId = folderId;
      }
      const { data } = await httpGetGlobalAssets(params);

      return data;
    } catch (error) {
      showErrorMessage(getErrorText());
      throw error;
    }
  },
);

const getGlobalAsset = createAsyncThunk(
  'assets/getGlobalAsset',
  async ({ savedName, params }) => {
    try {
      const { data } = await httpGetGlobalAsset(savedName, params);

      return data;
    } catch (error) {
      showErrorMessage(getErrorText());
      throw error;
    }
  },
);

const getAssets = createAsyncThunk(
  'assets/getAssets',
  async ({ id, type = 'image' }) => {
    try {
      const { data } = await httpGetAssets(id, type);

      return { data, type };
    } catch (error) {
      showErrorMessage(getErrorText());
      throw error;
    }
  },
);

const uploadAssets = createAsyncThunk(
  'assets/uploadAssets',
  async ({ payload }) => {
    try {
      const { data } = await httpUploadAssets(payload);

      return data;
    } catch (error) {
      if (error?.response?.status === 413) {
        showErrorMessage(
          'Unable to upload. Please try smaller or fewer files.',
        );
      } else {
        showErrorMessage(getErrorText());
      }
      throw error;
    }
  },
);

const deleteGlobalAsset = createAsyncThunk(
  'assets/deleteGlobalAsset',
  async ({ assetName, type = 'image' } = {}) => {
    try {
      await httpDeleteGlobalAsset(assetName, type);

      return { assetName, type };
    } catch (error) {
      showErrorMessage(getErrorText());
      throw error;
    }
  },
);

const deleteAsset = createAsyncThunk(
  'assets/deleteAsset',
  async ({ id, name, type = 'image' } = {}) => {
    try {
      const assetUrl =
        type === 'icon'
          ? buildIconAssetsUrl(id, name)
          : buildAssetsUrl(id, name);
      const payload = { url: assetUrl };

      await httpDeleteAsset(id, payload, type);

      return { name, type };
    } catch (error) {
      showErrorMessage(getErrorText());
      throw error;
    }
  },
);

const clearGlobalImages = createAction('clearGlobalImages');

const clearTemplateImages = createAction('clearTemplateImages');

const getUnsplash = createAsyncThunk(
  'assets/unsplash',
  async ({ url: urlString, params } = {}) => {
    try {
      const url = new URL(urlString);

      url.search = new URLSearchParams(params);

      const { data } = await httpUnsplash(url);

      return data;
    } catch (error) {
      showErrorMessage(getErrorText());
      throw error;
    }
  },
);

const setUnsplashSearchTerm = createAction('setUnsplashSearchTerm');

const setUnsplashNextPage = createAction('setUnsplashNextPage');

const clearUnsplash = createAction('clearUnsplash');

const searchIcons = createAsyncThunk('assets/searchIcons', async (params) => {
  try {
    const url = new URL('https://api.iconfinder.com/v4/icons/search');

    url.search = new URLSearchParams(params);

    const { data } = await httpSearchIcons(url);

    return data;
  } catch (error) {
    showErrorMessage(getErrorText());
    throw error;
  }
});

const setIconFinderSearchTerm = createAction('setIconFinderSearchTerm');

const setIconFinderNextPage = createAction('setIconFinderNextPage');

const clearIconFinder = createAction('clearIconFinder');

const getFolders = createAsyncThunk('assets/getFolders', async (type) => {
  try {
    const { data } = await httpGetFolders(type);

    return data;
  } catch (error) {
    showErrorMessage(getErrorText());
    throw error;
  }
});

const createFolder = createAsyncThunk(
  'assets/createFolder',
  async ({ name, type }) => {
    try {
      const { data } = await httpCreateFolder({ name, type });

      return data;
    } catch (error) {
      showErrorMessage(getErrorText());
      throw error;
    }
  },
);

const updateGlobalAsset = createAsyncThunk(
  'assets/updateGlobalAsset',
  async ({ assetName, type, originalName, folderId }) => {
    try {
      const { data } = await httpUpdateGlobalAsset(assetName, type, {
        originalName,
        folderId,
      });

      return data;
    } catch (error) {
      showErrorMessage(getErrorText());
      throw error;
    }
  },
);

export {
  copyFromGlobalToTemplateAssets,
  getGlobalAssets,
  getGlobalAsset,
  getAssets,
  uploadAssets,
  deleteGlobalAsset,
  deleteAsset,
  clearGlobalImages,
  clearTemplateImages,
  getUnsplash,
  setUnsplashSearchTerm,
  setUnsplashNextPage,
  clearUnsplash,
  searchIcons,
  setIconFinderSearchTerm,
  setIconFinderNextPage,
  clearIconFinder,
  getFolders,
  createFolder,
  updateGlobalAsset,
};
